import { apiAxios, API_BASE_URL } from "utils/axios";

/**
 * Calls behind the cabinet's API page (spec 2026-10-20-website-api-settings,
 * Stage 6). Every call carries the signed-in user's api key as a Bearer and
 * goes through `apiAxios`, so a key rotated in another tab is recovered by
 * the 401 interceptor (utils/auth/recoverApiKey) like any other data call.
 *
 * Route strings and envelopes are verbatim from ipscore-api
 * (`api/routes/client.py`, `api/routes/ipchecker.py`).
 */

/** One row of `user_api_key_service`, as `GET /get_api_checker_service` answers it. */
export interface ApiCheckerSettings {
  id?: number;
  user_id?: number;
  api_key_id?: number;
  /** 1 = automated full report, 0 = custom check (api `controllers/checker.py`). */
  mode: number | boolean | null;
  AbuseIpDb?: number | boolean | null;
  IpData?: number | boolean | null;
  IpApi?: number | boolean | null;
  IpInfo?: number | boolean | null;
  IpStack?: number | boolean | null;
  IpQualityScore?: number | boolean | null;
  /** IPQS strictness 0-3. */
  ipqs_strictness?: number | null;
  IpIntel?: number | boolean | null;
  Proxstop?: number | boolean | null;
  Scamalytics?: number | boolean | null;
  Whois?: number | boolean | null;
  BlackList?: number | boolean | null;
  /** 1 = detailed, 0 = fast (the bot's `bl_mode_callback`). */
  bl_mode?: number | boolean | null;
  PortList?: number | boolean | null;
}

export type ApiCheckerSettingName = Exclude<
  keyof ApiCheckerSettings,
  "id" | "user_id" | "api_key_id"
>;

export type AccountType = "telegram" | "google" | "email";

/** `GET /api_key_summary`: which account, plan and quota a key draws from. */
export interface ApiKeySummary {
  account: { type: AccountType; identity: string | null };
  plan: { name: string | null; date_end: string | null } | null;
  requests_left: number;
}

/** `GET /check-ip?dry=true`: the free report plus whether a real check would charge. */
export interface DryCheckResponse {
  status: string;
  data: unknown;
  dry: boolean;
  would_charge: boolean;
}

const bearer = (apiKey: string) => ({ Authorization: `Bearer ${apiKey}` });

/**
 * Some of these routes answer an error ENVELOPE with HTTP 200
 * (`get_api_checker_service` for an account without a key row). A 200 that
 * says `status: "error"` is a failure, never data.
 */
export class ApiEnvelopeError extends Error {
  constructor(message?: string) {
    super(message || "The API answered with an error");
    this.name = "ApiEnvelopeError";
  }
}

const unwrap = <T>(body: any): T => {
  if (!body || body.status === "error" || body.data === undefined) {
    throw new ApiEnvelopeError(body?.message);
  }
  return body.data as T;
};

export const getApiCheckerSettings = async (
  apiKey: string
): Promise<ApiCheckerSettings> => {
  const response = await apiAxios.get("/get_api_checker_service", {
    headers: bearer(apiKey),
  });
  return unwrap<ApiCheckerSettings>(response.data);
};

/**
 * Store one setting. Toggles are 0/1, `ipqs_strictness` 0-3, `bl_mode` 0/1
 * (the `{checker, status}` body the bot sends too). Resolves with the stored
 * row; a 400 envelope, a 200 error envelope or a network failure rejects.
 */
export const updateApiCheckerSetting = async (
  apiKey: string,
  checker: ApiCheckerSettingName,
  status: number
): Promise<ApiCheckerSettings> => {
  const response = await apiAxios.post(
    "/update_api_checker_service",
    { checker, status },
    { headers: bearer(apiKey) }
  );
  return unwrap<ApiCheckerSettings>(response.data);
};

/**
 * Replace the key. The old key is refused at once; the new one is returned.
 * 409 `shared_key` / `key_changed`, 503 `revocation_unavailable` reject.
 */
export const rotateApiKey = async (apiKey: string): Promise<string> => {
  const response = await apiAxios.post("/rotate_api_key", undefined, {
    headers: bearer(apiKey),
  });
  const data = unwrap<{ api_key?: string }>(response.data);
  if (!data?.api_key) {
    throw new ApiEnvelopeError("rotate_api_key returned no api_key");
  }
  return data.api_key;
};

/**
 * The check the test request and its command line describe: own IP, run with
 * the key's saved settings. No `mode` param on purpose: the api lets a query
 * `mode` override the stored one, so a pinned value would ignore the mode the
 * customer chose on this page.
 */
export const CHECK_IP_PATH = "/check-ip";

/** The cabinet's test request: own IP, no charge, the free report. */
export const dryCheckOwnIp = async (apiKey: string): Promise<DryCheckResponse> => {
  const response = await apiAxios.get(`${CHECK_IP_PATH}?dry=true`, {
    headers: bearer(apiKey),
  });
  return response.data as DryCheckResponse;
};

/**
 * The command line for the same check without the test flag (the call a
 * customer's script makes). `shownKey` is whatever the page currently shows,
 * so a masked key stays masked here too.
 */
export const buildCheckIpCurl = (shownKey: string): string =>
  `curl -s -H "Authorization: Bearer ${shownKey}" "${API_BASE_URL}${CHECK_IP_PATH}"`;

/** The `reason` of a refused call (`shared_key`, `revocation_unavailable`, …), if any. */
export const refusalReason = (error: any): string | undefined => {
  const body = error?.response?.data;
  return body?.reason ?? body?.detail?.reason;
};
